import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiCalendar, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import ContractDrillDownPanel from './ContractDrillDownPanel';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (iso) => {
  if (!iso) return '';
  const p = String(iso).split('-');
  if (p.length !== 3) return iso;
  return `${p[2].padStart(2,'0')}-${MONTHS_SHORT[parseInt(p[1],10)-1]}-${p[0]}`;
};

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const Delta = ({ current, previous, isValue }) => {
  if (previous == null) return null;
  const diff = current - previous;
  const up   = diff > 0;
  const same = diff === 0;
  if (same) return <FiMinus size={10} className="tw-text-gray-400" />;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-bold ${up ? 'tw-text-emerald-600' : 'tw-text-red-500'}`}>
      {up ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
      {isValue ? fmtM(Math.abs(diff)) : Math.abs(diff)}
    </span>
  );
};
Delta.propTypes = {
  current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previous: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isValue: PropTypes.bool,
};

const MiniBar = ({ thisVal, lastVal, color }) => {
  const max = Math.max(thisVal, lastVal, 1);
  return (
    <div className="tw-flex tw-items-end tw-gap-1 tw-h-10">
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-0.5">
        <div className="tw-w-6 tw-rounded-sm" style={{ height: Math.max((thisVal / max) * 36, 2), background: color }} />
        <span className="tw-text-xs tw-text-gray-400">This</span>
      </div>
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-0.5">
        <div className="tw-w-6 tw-rounded-sm" style={{ height: Math.max((lastVal / max) * 36, 2), background: color, opacity: 0.35 }} />
        <span className="tw-text-xs tw-text-gray-400">Last</span>
      </div>
    </div>
  );
};
MiniBar.propTypes = {
  thisVal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lastVal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
};

const MetricCard = ({ label, thisVal, lastVal, color, isValue, onClick }) => (
  <div
    onClick={onClick}
    className={`tw-rounded-lg tw-border tw-p-3 tw-transition-all ${onClick ? 'tw-cursor-pointer hover:tw-shadow-md hover:tw-scale-[1.01]' : ''}`}
    style={{ borderColor: color + '30', background: color + '08' }}
  >
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
      <div className="tw-flex-1">
        <p className="tw-text-xs tw-text-gray-500 tw-font-semibold tw-mb-0.5">{label}</p>
        <p className="tw-text-xl tw-font-extrabold" style={{ color }}>
          {isValue ? fmtM(thisVal) : thisVal}
        </p>
        <div className="tw-flex tw-items-center tw-gap-1 tw-mt-0.5">
          <span className="tw-text-xs tw-text-gray-400">vs {isValue ? fmtM(lastVal) : lastVal}</span>
          <Delta current={thisVal} previous={lastVal} isValue={isValue} />
        </div>
      </div>
      <MiniBar thisVal={thisVal} lastVal={lastVal} color={color} />
    </div>
  </div>
);
MetricCard.propTypes = {
  label: PropTypes.node,
  thisVal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lastVal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
  isValue: PropTypes.bool,
  onClick: PropTypes.func,
};

const ContractWeeklyChart = ({ data, loading, error, baseParams }) => {
  const [drillOpen,  setDrillOpen]  = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillExtra, setDrillExtra] = useState({});

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading weekly comparison</p>
    </Card>
  );

  const d  = data || {};
  const tw = d.this_week || {};
  const lw = d.last_week || {};

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiCalendar size={16} className="tw-text-blue-500" />
            <h3 className="tw-font-bold tw-text-gray-900">Weekly Comparison</h3>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-flex-wrap">
            {d.period?.this_week && (
              <span className="tw-bg-blue-50 tw-text-blue-600 tw-px-2 tw-py-0.5 tw-rounded-xl tw-font-medium">
                This: {fmtDate(d.period.this_week.start)} → {fmtDate(d.period.this_week.end)}
              </span>
            )}
            {d.period?.last_week && (
              <span className="tw-bg-gray-50 tw-text-gray-500 tw-px-2 tw-py-0.5 tw-rounded-xl">
                Last: {fmtDate(d.period.last_week.start)} → {fmtDate(d.period.last_week.end)}
              </span>
            )}
          </div>
        </div>

        <div className="tw-p-5 tw-space-y-3">
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <MetricCard label="Contracts" thisVal={tw.contract_count || 0} lastVal={lw.contract_count || 0}
              color="#3B82F6" onClick={() => {
                setDrillTitle('Contracts — This Week');
                setDrillExtra(d.period?.this_week ? { start_date: d.period.this_week.start, end_date: d.period.this_week.end } : {});
                setDrillOpen(true);
              }} />
            <MetricCard label="Contract Value" thisVal={tw.contract_value || 0} lastVal={lw.contract_value || 0}
              color="#10B981" isValue />
          </div>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <MetricCard label="Addendums" thisVal={tw.addendum_count || 0} lastVal={lw.addendum_count || 0}
              color="#F59E0B" onClick={() => {
                setDrillTitle('Addendums — This Week');
                setDrillExtra(d.period?.this_week ? { start_date: d.period.this_week.start, end_date: d.period.this_week.end } : {});
                setDrillOpen(true);
              }} />
            <MetricCard label="Addendum Value" thisVal={tw.addendum_value || 0} lastVal={lw.addendum_value || 0}
              color="#F97316" isValue />
          </div>

          <div className="tw-rounded-lg tw-bg-gradient-to-r tw-from-blue-50 tw-to-primary-50 tw-border tw-border-blue-100 tw-p-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
              <div>
                <p className="tw-text-xs tw-font-semibold tw-text-blue-500 tw-mb-0.5">Total This Week</p>
                <div className="tw-flex tw-items-baseline tw-gap-2">
                  <span className="tw-text-2xl tw-font-extrabold tw-text-blue-700">{tw.total_count || 0}</span>
                  <span className="tw-text-base tw-font-bold tw-text-blue-500">{fmtM(tw.total_value)}</span>
                </div>
              </div>
              <div className="tw-text-right">
                <p className="tw-text-xs tw-text-gray-400 tw-mb-0.5">vs Last Week</p>
                <div className="tw-flex tw-items-baseline tw-gap-2 tw-justify-end">
                  <span className="tw-text-lg tw-font-bold tw-text-gray-500">{lw.total_count || 0}</span>
                  <span className="tw-text-sm tw-text-gray-400">{fmtM(lw.total_value)}</span>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2">
                  <Delta current={tw.total_count || 0} previous={lw.total_count || 0} />
                  <Delta current={tw.total_value || 0} previous={lw.total_value || 0} isValue />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
};

ContractWeeklyChart.propTypes = {
  data:       PropTypes.object,
  loading:    PropTypes.bool,
  error:      PropTypes.any,
  baseParams: PropTypes.object
};
ContractWeeklyChart.defaultProps = { data: null, loading: false, error: null, baseParams: {} };

export default ContractWeeklyChart;
